import { useState } from 'react';
import { Button, Divider, Group, NumberInput, SegmentedControl, Stack, Text } from '@mantine/core';
import { DateInput, MonthPickerInput } from '@mantine/dates';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { BillingDetail } from './hooks/useBilling';
import { useUpdateBillingMutation } from './hooks/useUpdateBillingMutation';

type PricingChoice = 'PER_UNIT' | 'FIXED';

const STANDARD_PRICE = 5;

function toNumberOrEmpty(val: number | string | null | undefined): number | string {
  if (val === null || val === undefined) return '';
  const n = Number(val);
  return Number.isNaN(n) ? '' : n;
}

function toNullableNumber(val: number | string): number | null {
  if (val === '' || val === undefined) return null;
  const n = Number(val);
  return Number.isNaN(n) ? null : n;
}

function toFirstOfMonth(dateStr: string | null): string | null {
  if (!dateStr) return null;
  const d = new Date(dateStr + 'T00:00:00');
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Text size="textXs" c="dimmed" tt="uppercase" fw={FONT_WEIGHTS.semibold}>
      {children}
    </Text>
  );
}

export function BillingEditForm({
  billingDetail,
  onClose,
}: {
  billingDetail: BillingDetail;
  onClose: () => void;
}) {
  const { updateBilling, isLoading } = useUpdateBillingMutation();
  const [pricing, setPricing] = useState<PricingChoice>(billingDetail.billingType ?? 'PER_UNIT');
  const [amount, setAmount] = useState<number | string>(
    toNumberOrEmpty(billingDetail.billingAmount),
  );
  const [minimum, setMinimum] = useState<number | string>(
    toNumberOrEmpty(billingDetail.minimumBillingAmount),
  );
  const [billingStartDate, setBillingStartDate] = useState<string | null>(
    billingDetail.billingStartDate,
  );
  const [families, setFamilies] = useState<number | string>(
    toNumberOrEmpty(billingDetail.statedBillableUnits),
  );
  const [discountEndDate, setDiscountEndDate] = useState<string | null>(
    billingDetail.discountEndDate,
  );

  const perUnit = pricing === 'PER_UNIT';
  const price = toNullableNumber(amount);
  const minimumFee = toNullableNumber(minimum);
  // A discount is a per-family price below the standard one; nothing else needs storing.
  const discounted = perUnit && price !== null && price < STANDARD_PRICE;

  const handleSubmit = async () => {
    try {
      await updateBilling({
        companyId: billingDetail.companyId,
        // Left unset until there is something to bill, so an unpriced trial firm still shows "-".
        billingType: price === null && minimumFee === null ? null : pricing,
        billingAmount: price,
        minimumBillingAmount: minimumFee,
        billingStartDate: toFirstOfMonth(billingStartDate),
        // No longer edited here, so it is sent back as it was rather than cleared.
        statedClientCount: billingDetail.statedClientCount,
        statedBillableUnits: toNullableNumber(families),
        discountEndDate: discounted ? discountEndDate : null,
      });
      onClose();
    } catch (error) {
      notifyAndLogError('Failed to update billing', 'Please try again.', error);
    }
  };

  return (
    <Stack gap="md">
      <SectionLabel>Pricing</SectionLabel>

      <SegmentedControl
        fullWidth
        value={pricing}
        onChange={value => setPricing(value as PricingChoice)}
        data={[
          { value: 'PER_UNIT', label: 'Per family' },
          { value: 'FIXED', label: 'Fixed fee' },
        ]}
      />

      <NumberInput
        label={perUnit ? 'Price per family, a month' : 'Fixed fee, a month'}
        description={perUnit ? 'The standard price is £5' : undefined}
        prefix="£"
        thousandSeparator=","
        min={0}
        decimalScale={2}
        fixedDecimalScale
        value={amount}
        onChange={setAmount}
      />

      {discounted && (
        <DateInput
          label="Discount ends (optional)"
          description="For reporting only - the price above is billed until you change it"
          placeholder="No end date"
          valueFormat="D MMMM YYYY"
          value={discountEndDate}
          onChange={setDiscountEndDate}
          clearable
        />
      )}

      <NumberInput
        label="Minimum monthly bill (optional)"
        prefix="£"
        thousandSeparator=","
        min={0}
        decimalScale={2}
        fixedDecimalScale
        value={minimum}
        onChange={setMinimum}
      />

      <MonthPickerInput
        label="Billing starts"
        description="Leave empty while the firm is on a free trial"
        placeholder="Not started"
        value={billingStartDate}
        onChange={setBillingStartDate}
        clearable
      />

      <Divider />
      <SectionLabel>Firm size</SectionLabel>

      <NumberInput
        label="Total families (stated by the firm)"
        description="Count each family once, and a client not in a family as one"
        min={0}
        allowDecimal={false}
        thousandSeparator=","
        value={families}
        onChange={setFamilies}
      />
      {billingDetail.statedClientCount !== null && (
        <Text size="textXs" c="dimmed">
          Previously stated: {billingDetail.statedClientCount.toLocaleString('en-GB')} clients. A
          couple counts as two clients but one family.
        </Text>
      )}

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} loading={isLoading}>
          Save
        </Button>
      </Group>
    </Stack>
  );
}
